import { useLayoutEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  startApiLoading,
  endApiLoading,
} from "../../utilities/apiLoadingManager";

/**
 * Suspense / chunk fallback — ONE header loader strip (no layout twin).
 * Page screens use GenericHeader's spinner instead; do not nest this under GenericHeader.
 */
const PageLoader = ({ context = "Loading..." }) => {
  const theme = useTheme();
  const textColor = theme.palette.primary.contrastText;

  useLayoutEffect(() => {
    startApiLoading(context);
    return () => endApiLoading();
  }, [context]);

  return (
    <Box
      aria-busy="true"
      aria-label={context}
      sx={{
        flex: 1,
        width: "100%",
        minHeight: "100%",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: (t) => t.zIndex.appBar,
          minHeight: 70,
          height: 70,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          bgcolor: theme.palette.primary.main,
          color: textColor,
          boxShadow: theme.customShadows?.sm || theme.shadows[2],
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          overflow: "hidden",
          [theme.breakpoints.down("md")]: {
            minHeight: "auto",
            height: "auto",
            py: 1.5,
            px: 2,
          },
        }}
      >
        <Tooltip title={context} arrow placement="bottom">
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              maxWidth: "100%",
            }}
          >
            <CircularProgress
              size={18}
              thickness={5}
              sx={{ color: textColor, flexShrink: 0, animationDuration: "1.1s" }}
            />
            <Typography
              component="span"
              variant="caption"
              noWrap
              sx={{
                color: textColor,
                opacity: 0.92,
                fontWeight: 600,
                fontSize: "0.75rem",
                lineHeight: 1.2,
                letterSpacing: "0.01em",
                maxWidth: { xs: 160, sm: 240, md: 320 },
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {context}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default PageLoader;
